import type { IHomeLocation } from '@/domain/entities/home-location';
import { isNotFoundError, normalizeArea } from '@/lib';
import { normalizeDeep } from '@/lib/dataSanity';
import { getApiEndpoints } from '@/services/api/endpoints';
import type { CreateItemResponse, ListLocationsResponse } from '@/services/api/types';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';

type ApiError = { message: string; status?: number; body?: string };

/**
 * LIST locations by propertyID
 */
export const fetchLocationsByPropertyId = createAppAsyncThunk<IHomeLocation[], { propertyID: string }>(
  'locations/listByProperty',
  async ({ propertyID }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.locations.getLocations + `?propertyID=${encodeURIComponent(propertyID)}`;

      const res = await apiService.get<ListLocationsResponse>(endpoint);
      return Array.isArray(res.items) ? res.items : [];
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      if (isNotFoundError(error)) {
        logger?.info('[fetchLocationsByPropertyId] No locations found, returning empty list', { propertyID });
        return [];
      }

      return rejectWithValue({ message: error.message } satisfies ApiError);
    }
  },
);

/**
 * GET location by id
 */
export const fetchLocationById = createAppAsyncThunk<IHomeLocation, { id: string }>(
  'locations/getById',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      // TODO: ensure endpoints.core.locations.getLocations exists
      const endpoint = `${getApiEndpoints().core.locations.getLocations}?id=${encodeURIComponent(id)}`;
      return await apiService.get<IHomeLocation>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message } satisfies ApiError);
    }
  },
);

/**
 * CREATE location (PUT upsert style like your properties)
 */
export const createLocation = createAppAsyncThunk<
  CreateItemResponse<IHomeLocation>,
  { location: IHomeLocation; tempId?: string }
>('locations/create', async ({ location }, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const endpoint = getApiEndpoints().core.locations.createLocation;
    return await apiService.post<CreateItemResponse<IHomeLocation>>(endpoint, normalizeDeep(location));
  } catch (err) {
    const e = err as Error & { status?: number; body?: string };
    return rejectWithValue({ message: e.message } satisfies ApiError);
  }
});

/**
 * UPDATE location (patch-like payload)
 */
export const updateLocation = createAppAsyncThunk<
  IHomeLocation,
  { id: string; propertyID: string; patch: Partial<IHomeLocation> }
>('locations/update', async ({ id, propertyID, patch }, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  //Temporary fix for old saved area as numbers
  const normalized: Partial<IHomeLocation> = {
    ...patch,
    area: normalizeArea(patch.area),
  };

  try {
    // TODO: ensure endpoints.core.locations.updateLocation exists
    const endpoint = `${getApiEndpoints().core.locations.updateLocation}?id=${encodeURIComponent(id)}&propertyID=${encodeURIComponent(propertyID)}`;
    return await apiService.put<IHomeLocation>(endpoint, normalizeDeep(normalized));
  } catch (err) {
    const e = err as Error & { status?: number; body?: string };
    return rejectWithValue({ message: e.message } satisfies ApiError);
  }
});

/**
 * DELETE location
 */
export const deleteLocation = createAppAsyncThunk<void, { id: string; propertyID: string }>(
  'locations/delete',
  async ({ id, propertyID }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      // TODO: ensure endpoints.core.locations.deleteLocation exists
      const endpoint = `${getApiEndpoints().core.locations.deleteLocation}?id=${encodeURIComponent(id)}&propertyID=${encodeURIComponent(propertyID)}`;
      await apiService.delete(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message } satisfies ApiError);
    }
  },
);
